import { useMutation, useQueryClient } from "@tanstack/react-query"
import React, { useState } from "react"
import { toast } from "sonner"

import { protestOverDue } from "@/api"
import { BottomSheetModal, LoadingSpinner } from "@/components/shared"
import { Button, Textarea } from "@/components/ui"
import { REASON_COMPLAINT } from "@/modules/helpers/common"
import { ReasonEnum } from "@/modules/helpers/enum"
import { cn } from "@/modules/helpers/hooks/shared/utils"

interface ComplaintModalProps {
  kolSubmissionId: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const ComplaintModal: React.FC<ComplaintModalProps> = ({ kolSubmissionId, isOpen, setIsOpen }) => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationKey: ["protest-overdues"],
    mutationFn: ({ reason, kolSubmissionId }: { reason: string; kolSubmissionId: string }) =>
      protestOverDue({
        reason,
        kolSubmissionId
      }),
    onSuccess() {
      toast.success("Khiếu nại thành công")
      setIsOpen(false)
      setSelectedReason(null)
      setOtherReason("")
      queryClient.invalidateQueries({ queryKey: ["submission"] })
    },
    onError(error: any) {
      toast.error(error?.response?.data?.error?.type || "Gửi thất bại")
    }
  })

  const [selectedReason, setSelectedReason] = useState<ReasonEnum | null>(null)
  const [otherReason, setOtherReason] = useState<string>("")

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!selectedReason || isPending) return

    const reason = selectedReason === ReasonEnum.OTHER ? otherReason.trim() : selectedReason
    if (selectedReason === ReasonEnum.OTHER && !reason) return

    mutate({
      reason,
      kolSubmissionId
    })
  }

  return (
    <>
      <BottomSheetModal
        isVisible={isOpen}
        setIsVisible={(visible) => {
          if (!visible) {
            handleClose()
          }
        }}
        title="Lý do quá hạn"
      >
        <div className="flex flex-col gap-4 px-4 pb-6 pt-2">
          {/* Separator line */}
          <div className="h-px bg-gray-200 -mt-4 mb-2" />

          {/* Reason options */}
          <div className="flex flex-col gap-3">
            {Object.entries(REASON_COMPLAINT).map(([value, label]) => {
              const isSelected = selectedReason === value
              return (
                <button
                  key={value}
                  type="button"
                  className="flex items-center gap-3 text-left"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedReason(value as ReasonEnum)
                  }}
                >
                  <span
                    className={[
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                      isSelected ? "border-primary-800" : "border-primary-200"
                    ].join(" ")}
                  >
                    {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-primary-800" />}
                  </span>
                  <span className="text-sm text-black">{label}</span>
                </button>
              )
            })}
          </div>

          {/* Other reason textarea */}
          {selectedReason === ReasonEnum.OTHER && (
            <div className="mt-1">
              <Textarea
                placeholder="Nhập vào lý do"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                className="min-h-[96px] rounded-2xl text-sm placeholder:text-gray-400"
              />
            </div>
          )}

          {/* Submit button */}
          <Button
            className={cn(
              "mt-2 h-12 w-full rounded-xl bg-primary text-sm font-semibold text-white",
              {
                "!bg-gray-300": isPending
              }
            )}
            onClick={handleSubmit}
            disabled={
              isPending ||
              !selectedReason ||
              (selectedReason === ReasonEnum.OTHER && !otherReason.trim())
            }
          >
            {isPending ? <LoadingSpinner className="w-20 h-20" /> : "Gửi"}
          </Button>
        </div>
      </BottomSheetModal>
    </>
  )
}

export default ComplaintModal
